import { useCallback, useEffect, useState } from "react";
import { BsBank2 } from "react-icons/bs";
import { LuCopyPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroCliente from "../../components/modais/ModalCadastroCliente";
import { repositorioCliente, repositorioContaBancaria } from "../../sync/repositories/financeiro";

const AJUDA = {
  titulo: "Cadastro de Contas Bancárias",
  descricao:
    "Cadastre aqui as contas bancárias, carteiras físicas ou aplicações da fazenda por onde o dinheiro entra e sai. Elas servem de referência para emissão de boletos (dados de convênio/carteira) e podem ser vinculadas a um cliente (cedente) para identificar o recebedor.",
  secoes: [
    {
      titulo: "Informações Básicas",
      campos: [
        {
          nome: "Nome da Conta",
          obrigatorio: true,
          explicacao: "Identificação da conta para você reconhecer facilmente (ex.: 'Conta Principal', 'Caixa da Fazenda').",
        },
        {
          nome: "Tipo de Conta",
          obrigatorio: true,
          explicacao: "Corrente, Poupança, Carteira Física ou Investimento — define a natureza da conta.",
        },
        {
          nome: "Cedente",
          obrigatorio: false,
          explicacao:
            "Cliente vinculado a essa conta, usado como referência de quem recebe. Se o cliente ainda não existe, use o botão '+' ao lado do campo para cadastrá-lo sem sair desta tela.",
        },
      ],
    },
    {
      titulo: "Dados Bancários",
      campos: [
        {
          nome: "Banco",
          obrigatorio: true,
          explicacao: "Código e nome do banco onde a conta está aberta.",
        },
        {
          nome: "Agência",
          obrigatorio: true,
          explicacao: "Número da agência e, se houver, o dígito verificador.",
        },
        {
          nome: "Conta",
          obrigatorio: true,
          explicacao: "Número da conta e, se houver, o dígito verificador.",
        },
      ],
    },
    {
      titulo: "Dados para Boleto",
      campos: [
        {
          nome: "Convênio / Carteira / Código do Beneficiário",
          obrigatorio: false,
          explicacao: "Dados fornecidos pelo banco, necessários apenas se essa conta for usada para emitir boletos.",
        },
      ],
    },
    {
      titulo: "Saldo",
      campos: [
        {
          nome: "Saldo Inicial",
          obrigatorio: false,
          explicacao: "Valor que já existia na conta antes de começar a usar o sistema — serve de ponto de partida para o saldo calculado.",
        },
      ],
    },
  ],
  dicas: [
    "Excluir uma conta bancária com movimentações vinculadas não é permitido — o sistema vai avisar caso isso aconteça.",
  ],
};

export default function GestaoContasBancarias() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [contas, setContas] = useState([]);
  const [contasFiltradas, setContasFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Dados para os selects
  const [bancos, setBancos] = useState([]);
  const [cedentes, setCedentes] = useState([]);
  const [mostrarModalCliente, setMostrarModalCliente] = useState(false);

  // Filtros
  const [filtros, setFiltros] = useState({
    busca: "",
    tipo: "",
    banco: ""
  });
  
  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);
  
  const [formData, setFormData] = useState({
    nome: "",
    tipo: "corrente",
    saldo_inicial: "",
    banco: "",
    agencia: "",
    agencia_digito: "",
    conta: "",
    conta_digito: "",
    convenio: "",
    carteira: "",
    codigo_beneficiario: "",
    cedente: "",
  });

  // Tipos de conta
  const tiposConta = [
    { value: "corrente", label: "Conta Corrente", descricao: "Conta bancária tradicional" },
    { value: "poupanca", label: "Conta Poupança", descricao: "Conta de poupança" },
    { value: "carteira", label: "Carteira Física", descricao: "Dinheiro em espécie" },
    { value: "investimento", label: "Investimento", descricao: "Aplicações financeiras" },
  ];

  // Lista de bancos brasileiros (melhorias - seria ideal vir do backend)
  const bancosBrasileiros = [
    { codigo: "001", nome: "Banco do Brasil" },
    { codigo: "033", nome: "Santander" },
    { codigo: "104", nome: "Caixa Econômica Federal" },
    { codigo: "237", nome: "Bradesco" },
    { codigo: "341", nome: "Itaú Unibanco" },
    { codigo: "260", nome: "Nu Pagamentos S.A. (Nubank)" },
    { codigo: "077", nome: "Banco Inter" },
    { codigo: "212", nome: "Banco Original" },
    { codigo: "380", nome: "PicPay" },
    { codigo: "290", nome: "PagSeguro" },
    { codigo: "735", nome: "Banco Neon" },
    { codigo: "637", nome: "Banco Sofisa" },
    { codigo: "655", nome: "Banco Votorantim" },
    { codigo: "707", nome: "Banco Daycoval" },
    { codigo: "756", nome: "Banco Cooperativo do Brasil (Sicoob)" },
  ];

  // Carregar dados iniciais
  const carregarCedentes = useCallback(async () => {
    try {
      const response = await repositorioCliente.listar();
      const dados = response.data.results || response.data;
      setCedentes(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar cedentes:", error);
      setCedentes([]);
    }
  }, []);

  // Carregar contas
  const carregarContas = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioContaBancaria.listar();
      const dados = response.data.results || response.data;
      setContas(Array.isArray(dados) ? dados : []);
      setContasFiltradas(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar contas:", error);
      alert("Erro ao carregar lista de contas");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarContas();
    carregarCedentes();
  }, [carregarContas, carregarCedentes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBancoChange = (e) => {
    const codigo = e.target.value;
    const bancoSelecionado = bancosBrasileiros.find(b => b.codigo === codigo);
    setFormData(prev => ({
      ...prev,
      banco: codigo,
      banco_nome: bancoSelecionado?.nome || ""
    }));
  };

  const validarFormulario = () => {
    if (!formData.nome.trim()) {
      alert("Informe o nome da conta.");
      return false;
    }
    if (!formData.tipo) {
      alert("Selecione o tipo da conta.");
      return false;
    }
    if (!formData.banco) {
      alert("Informe o código do banco.");
      return false;
    }
    if (!formData.agencia) {
      alert("Informe o número da agência.");
      return false;
    }
    if (!formData.conta) {
      alert("Informe o número da conta.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      nome: formData.nome,
      tipo: formData.tipo,
      saldo_inicial: formData.saldo_inicial ? Number(formData.saldo_inicial) : 0,
      banco: formData.banco,
      agencia: formData.agencia,
      agencia_digito: formData.agencia_digito || "",
      conta: formData.conta,
      conta_digito: formData.conta_digito || "",
      convenio: formData.convenio || "",
      carteira: formData.carteira || "",
      codigo_beneficiario: formData.codigo_beneficiario || "",
      cedente: formData.cedente || null,
    };

    try {
      if (editando) {
        await repositorioContaBancaria.atualizar(editandoId, payload);
        alert("Conta bancária atualizada com sucesso!");
        handleCancel();
        carregarContas();
      } else {
        await repositorioContaBancaria.criar(payload);
        alert("Conta bancária cadastrada com sucesso!");
        handleCancel();
        carregarContas();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (conta) => {
    setFormData({
      nome: conta.nome || "",
      tipo: conta.tipo || "corrente",
      saldo_inicial: conta.saldo_inicial || "",
      banco: conta.banco || "",
      agencia: conta.agencia || "",
      agencia_digito: conta.agencia_digito || "",
      conta: conta.conta || "",
      conta_digito: conta.conta_digito || "",
      convenio: conta.convenio || "",
      carteira: conta.carteira || "",
      codigo_beneficiario: conta.codigo_beneficiario || "",
      cedente: conta.cedente?.id || conta.cedente || "",
    });
    setEditando(true);
    setEditandoId(conta.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovaConta = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      tipo: "corrente",
      saldo_inicial: "",
      banco: "",
      agencia: "",
      agencia_digito: "",
      conta: "",
      conta_digito: "",
      convenio: "",
      carteira: "",
      codigo_beneficiario: "",
      cedente: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta conta bancária? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioContaBancaria.excluir(id);
      alert("Conta bancária excluída com sucesso!");
      carregarContas();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir conta bancária. Verifique se não há movimentações vinculadas.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(contas) ? [...contas] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(c => 
        c.nome?.toLowerCase().includes(buscaLower) ||
        c.agencia?.includes(buscaLower) ||
        c.conta?.includes(buscaLower)
      );
    }
    
    if (filtros.tipo) {
      filtrados = filtrados.filter(c => c.tipo === filtros.tipo);
    }
    
    if (filtros.banco) {
      filtrados = filtrados.filter(c => c.banco === filtros.banco);
    }
    
    setContasFiltradas(filtrados);
    setPaginaAtual(1);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", tipo: "", banco: "" });
    setContasFiltradas(contas);
    setPaginaAtual(1);
  };

  const getTipoLabel = (tipo) => {
    const tipoEncontrado = tiposConta.find(t => t.value === tipo);
    return tipoEncontrado ? tipoEncontrado.label : tipo;
  };

  const getTipoColor = (tipo) => {
    const cores = {
      corrente: "bg-blue-100 text-blue-800",
      poupanca: "bg-green-100 text-green-800",
      carteira: "bg-yellow-100 text-yellow-800",
      investimento: "bg-purple-100 text-purple-800",
    };
    return cores[tipo] || "bg-gray-100 text-gray-800";
  };

  const getBancoNome = (codigo) => {
    const banco = bancosBrasileiros.find(b => b.codigo === codigo);
    return banco ? `${banco.codigo} - ${banco.nome}` : codigo || "—";
  };

  const formatarSaldo = (saldo) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(saldo || 0);
  };

  const formatarContaCompleta = (conta, digito) => {
    if (!conta) return "—";
    return digito ? `${conta}-${digito}` : conta;
  };

  const formatarAgenciaCompleta = (agencia, digito) => {
    if (!agencia) return "—";
    return digito ? `${agencia}-${digito}` : agencia;
  };

  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const indiceUltimoItem = paginaAtual * itensPorPagina;
  const indicePrimeiroItem = indiceUltimoItem - itensPorPagina;
  const contasPaginadas = contasFiltradas.slice(indicePrimeiroItem, indiceUltimoItem);
  const totalPaginas = Math.ceil(contasFiltradas.length / itensPorPagina);

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Contas Bancárias <BsBank2 sieze={38}/></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaConta}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Conta Bancária
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Conta Bancária" : "Nova Conta Bancária"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Seção 1: Informações Básicas */}
                <div className="border-b pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Nome da Conta *</label>
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Ex: Conta Principal, Reserva de Emergência..."
                        required
                      />
                      <span className="text-xs text-gray-500 mt-1">Identificação da conta</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Conta *</label>
                      <select
                        name="tipo"
                        value={formData.tipo}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione...</option>
                        {tiposConta.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-500 mt-1">
                        {tiposConta.find(t => t.value === formData.tipo)?.descricao}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Cedente</label>
                      <div className="flex items-center gap-2">
                        <select
                          name="cedente"
                          value={formData.cedente}
                          onChange={handleChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        >
                          <option value="">Selecione um cedente...</option>
                          {cedentes.map(c => (
                            <option key={c.id} value={c.id}>{c.nome_completo || c.razao_social}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setMostrarModalCliente(true)}
                          title="Cadastrar novo cedente"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                        </button>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">Cliente vinculado (opcional)</span>
                    </div>
                  </div>
                </div>

                {/* Seção 2: Dados Bancários */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">Dados Bancários</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Banco *</label>
                      <div className="flex items-center gap-2">
                        <select
                          value={formData.banco}
                          onChange={handleBancoChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          required
                        >
                          <option value="">Selecione o banco...</option>
                          {bancosBrasileiros.map(b => (
                            <option key={b.codigo} value={b.codigo}>
                              {b.codigo} - {b.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">Código e nome do banco</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Agência</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          name="agencia"
                          value={formData.agencia}
                          onChange={handleChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          placeholder="0000"
                          required
                        />
                        <input
                          type="text"
                          name="agencia_digito"
                          value={formData.agencia_digito}
                          onChange={handleChange}
                          className="w-16 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          placeholder="Dígito"
                        />
                      </div>
                      <span className="text-xs text-gray-500 mt-1">Número da agência e dígito verificador</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Conta</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          name="conta"
                          value={formData.conta}
                          onChange={handleChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          placeholder="00000"
                          required
                        />
                        <input
                          type="text"
                          name="conta_digito"
                          value={formData.conta_digito}
                          onChange={handleChange}
                          className="w-16 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          placeholder="Dígito"
                        />
                      </div>
                      <span className="text-xs text-gray-500 mt-1">Número da conta e dígito verificador</span>
                    </div>
                  </div>
                </div>

                {/* Seção 3: Dados para Boleto */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">Dados para Boleto</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Convênio</label>
                      <input
                        type="text"
                        name="convenio"
                        value={formData.convenio}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Número do convênio"
                      />
                      <span className="text-xs text-gray-500 mt-1">Número do convênio com o banco</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Carteira</label>
                      <input
                        type="text"
                        name="carteira"
                        value={formData.carteira}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Número da carteira"
                      />
                      <span className="text-xs text-gray-500 mt-1">Modalidade de cobrança</span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Código do Beneficiário</label>
                      <input
                        type="text"
                        name="codigo_beneficiario"
                        value={formData.codigo_beneficiario}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Código do beneficiário"
                      />
                      <span className="text-xs text-gray-500 mt-1">Código para emissão de boletos</span>
                    </div>
                  </div>
                </div>

                {/* Seção 4: Saldo Inicial */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Saldo Inicial</label>
                  <div className="flex items-center border border-green-500 rounded w-64">
                    <span className="px-3 text-gray-700 bg-gray-100 rounded-l">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      name="saldo_inicial"
                      value={formData.saldo_inicial}
                      onChange={handleChange}
                      className="flex-1 p-2 rounded-r focus:ring-2 focus:ring-green-300"
                      placeholder="0,00"
                    />
                  </div>
                  <span className="text-xs text-gray-500 mt-1">Saldo inicial da conta (opcional)</span>
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {/* Filtros e Tabela - visíveis apenas quando formulário está fechado */}
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome</label>
                  <input
                    type="text"
                    placeholder="Nome da conta..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Conta</label>
                  <select
                    value={filtros.tipo}
                    onChange={(e) => setFiltros({...filtros, tipo: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todos os tipos</option>
                    {tiposConta.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Banco</label>
                  <select
                    value={filtros.banco}
                    onChange={(e) => setFiltros({...filtros, banco: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todos os bancos</option>
                    {bancosBrasileiros.map(b => (
                      <option key={b.codigo} value={b.codigo}>{b.codigo} - {b.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Contas Bancárias */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center flex-wrap gap-2">
                <p className="text-gray-600">
                  {contasFiltradas.length} conta(s) bancária(s) encontrada(s)
                </p>
                <div className="text-sm text-gray-500">
                  📄 Página {paginaAtual} de {totalPaginas || 1}
                </div>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">⏳ Carregando...</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">ID</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Tipo</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Banco</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Agência</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Conta</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Saldo</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Cedente</th>
                          <th className="p-3 text-center text-sm font-semibold text-gray-700">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contasPaginadas.map(conta => (
                          <tr key={conta.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                            <td className="p-3 text-sm font-mono">{conta.id}</td>
                            <td className="p-3 text-sm font-medium">{conta.nome}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTipoColor(conta.tipo)}`}>
                                {getTipoLabel(conta.tipo)}
                              </span>
                            </td>
                            <td className="p-3 text-sm">{getBancoNome(conta.banco)}</td>
                            <td className="p-3 text-sm font-mono">{formatarAgenciaCompleta(conta.agencia, conta.agencia_digito)}</td>
                            <td className="p-3 text-sm font-mono">{formatarContaCompleta(conta.conta, conta.conta_digito)}</td>
                            <td className="p-3 text-sm font-medium">{formatarSaldo(conta.saldo_inicial)}</td>
                            <td className="p-3 text-sm">{conta.cedente_nome || "—"}</td>
                            <td className="p-3">
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() => handleEditar(conta)}
                                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Editar"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() => handleExcluir(conta.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Excluir"
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        
                        {contasPaginadas.length === 0 && (
                          <tr>
                            <td colSpan="9" className="p-8 text-center text-gray-500">
                              {filtros.busca || filtros.tipo || filtros.banco
                                ? "Nenhuma conta encontrada com os filtros selecionados."
                                : "Nenhuma conta bancária cadastrada."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  {totalPaginas > 1 && (
                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-center items-center gap-2">
                      <button
                        onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
                        disabled={paginaAtual === 1}
                        className={`px-3 py-1 rounded transition-colors ${paginaAtual === 1 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                      >
                        ◀ Anterior
                      </button>
                      <div className="flex gap-1">
                        {Array.from({ length: Math.min(totalPaginas, 5) }, (_, i) => {
                          let num;
                          if (totalPaginas <= 5) num = i + 1;
                          else if (paginaAtual <= 3) num = i + 1;
                          else if (paginaAtual >= totalPaginas - 2) num = totalPaginas - 4 + i;
                          else num = paginaAtual - 2 + i;
                          return (
                            <button
                              key={num}
                              onClick={() => setPaginaAtual(num)}
                              className={`w-8 h-8 rounded transition-colors ${paginaAtual === num ? "bg-green-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                            >
                              {num}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
                        disabled={paginaAtual === totalPaginas}
                        className={`px-3 py-1 rounded transition-colors ${paginaAtual === totalPaginas ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                      >
                        Próxima ▶
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroCliente
        isOpen={mostrarModalCliente}
        onClose={() => setMostrarModalCliente(false)}
        onSuccess={async (novo) => {
          await carregarCedentes();
          setFormData((prev) => ({ ...prev, cedente: novo.id }));
        }}
      />
    </Layout>
  );
}